import { Button, Checkbox, LoadingModal, ModalFooter, Select } from "@mixitone/components";
import type { TournamentSession } from "@mixitone/models";
import {
  TournamentCompetition,
  TournamentEntry,
  type TournamentMatch,
  TournamentPlayer,
  TournamentRegistration,
} from "@mixitone/models";
import { ApplicationView } from "@mixitone/mvc";
import { withStateToggle } from "@mixitone/util";
import AppModal from "components/common/AppModal";
import MixitoneController from "controllers/MixitoneController";
import { injectModal } from "lib/injectParams";
import { jsPDF } from "jspdf";
import { List } from "@mixitone/oom";
import {
  roundRobinGroupsLocked,
  roundRobinHasGroups,
  setRoundRobinGroupsLocked,
} from "components/Pages/Tournament/tournamentHelpers";
import { messagesService } from "services";
import { TournamentDataAdapter } from "tournament-formats/TournamentDataAdapter";

interface State {
  loading: boolean;
  competition: TournamentCompetition;
  matches: List<TournamentMatch>;
  entries: List<TournamentEntry>;
  players: List<TournamentPlayer>;
  registrations: List<TournamentRegistration>;
  tournamentSession: TournamentSession | null;
  orientation: "landscape" | "portrait";
  paperSize: "a4" | "letter" | "a5";
  lockGroupsAfterPrint: boolean;
}

interface Props {
  competitionId: string;
}

class CompetitionPrintController extends MixitoneController<State, Props> {
  getDefaultPaperSize(): "a4" | "letter" | "a5" {
    // Default to Letter for US, Canada, Mexico; A4 for others
    const locale = typeof navigator !== "undefined" ? navigator.language : "en";
    const countryCode = locale.split("-")[1]?.toUpperCase();
    if (countryCode === "US" || countryCode === "CA" || countryCode === "MX") {
      return "letter";
    }
    return "a4";
  }

  override get initialState(): State {
    return {
      loading: true,
      competition: new TournamentCompetition({}),
      matches: new List(),
      entries: new List(),
      players: new List(),
      registrations: new List(),
      tournamentSession: null,
      orientation: "landscape",
      paperSize: this.getDefaultPaperSize(),
      lockGroupsAfterPrint: true,
    };
  }

  override async initialize(props: Props) {
    await this.loadCompetition(props.competitionId);
    this.updateDefaultOrientation();
  }

  updateDefaultOrientation() {
    // Calculate max number of opponents for any entry
    let maxOpponents = 0;
    for (const entry of this.state.entries) {
      const entryId = entry.id;
      if (!entryId) continue;
      const entryMatches = this.state.matches.filter((m) => m.entries?.current.includes(entryId));
      // Include the extra blank opponent row printed at the bottom of each sheet
      maxOpponents = Math.max(maxOpponents, entryMatches.length + 1);
    }

    // Default to landscape if < 11 opponents, otherwise portrait
    this.state.orientation = maxOpponents < 11 ? "landscape" : "portrait";
  }

  getPrintableEntryCount(): number {
    return this.state.entries.filter((entry) => entry.id && entry.group).length;
  }

  @withStateToggle("loading")
  async loadCompetition(id: string) {
    const competition = await TournamentCompetition.query().find(id);
    this.state.competition = competition;
    this.state.matches = new List(...(await new TournamentDataAdapter().loadProjectedMatches(competition)));
    this.state.entries = new List(
      ...(await TournamentEntry.query().eq("tournament_competition_id", id).all()),
    );
    this.state.registrations = new List(
      ...(await TournamentRegistration.query().eq("tournament_competition_id", id).all()),
    );
    this.state.players = new List(
      ...(await TournamentPlayer.query()
        .in(
          "id",
          this.state.registrations.map((r) => r.tournament_player_id!),
        )
        .all()),
    );

    // Load tournament session if not already loaded
    if (!this.state.competition.session.loaded) {
      await this.state.competition.session.load();
    }
    this.state.tournamentSession = this.state.competition.session.value || null;
  }

  async actionPrint() {
    const orientation = this.state.orientation;
    const paperSize = this.state.paperSize;
    // Reload everything before processing
    const competitionId = this.state.competition.id;
    if (!competitionId) return;
    await this.loadCompetition(competitionId);

    // Sort entries by group, then by name (only entries that have a group)
    const sortedEntries = [...this.state.entries]
      .filter((entry) => entry.id && entry.group)
      .sort((a, b) => {
        const groupA = (a.group || "").toLowerCase();
        const groupB = (b.group || "").toLowerCase();
        if (groupA !== groupB) {
          return groupA.localeCompare(groupB);
        }
        const nameA = a.names.join(" & ").toLowerCase();
        const nameB = b.names.join(" & ").toLowerCase();
        return nameA.localeCompare(nameB);
      });

    if (sortedEntries.length === 0) {
      return;
    }

    const doc = new jsPDF({
      orientation: orientation === "landscape" ? "landscape" : "portrait",
      unit: "mm",
      format: paperSize,
    });

    // Table configuration
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const tableStartX = margin;
    const tableStartY = 40;
    const tableWidth = pageWidth - 2 * margin;
    // First column is wider (60%), other two are thinner (20% each)
    const col1Width = tableWidth * 0.6;
    const col2Width = tableWidth * 0.2;
    const col3Width = tableWidth * 0.2;
    const rowHeight = 15; // Increased row height
    const headerHeight = 10; // Increased header height

    for (const entry of sortedEntries) {
      const entryId = entry.id;
      if (!entryId) continue;

      // Entry header
      doc.setFontSize(18); // Increased font size
      doc.text(entry.names.join(" & "), margin, 20);
      doc.setFontSize(14); // Increased font size
      doc.setTextColor(50, 50, 50);
      doc.text(`${entry.group || ""}`, margin, 28); // Moved closer
      doc.setTextColor(0, 0, 0);

      // Tournament session name and competition name on the right
      doc.setFontSize(18); // Same size as entry names
      doc.text(this.state.tournamentSession?.name || "", pageWidth - margin, 20, { align: "right" });
      doc.setFontSize(14); // Increased font size
      doc.setTextColor(50, 50, 50);
      doc.text(this.state.competition.name || "", pageWidth - margin, 28, { align: "right" }); // Moved closer
      doc.setTextColor(0, 0, 0);

      const entryMatches = this.state.matches.filter((m) => m.entries?.current.includes(entryId));

      // Draw table header
      doc.setFontSize(14); // Increased font size for headers
      doc.setFont("helvetica", "bold");
      const headerY = tableStartY;

      // Header row background and borders
      doc.setDrawColor(0, 0, 0);
      doc.setFillColor(240, 240, 240);
      doc.rect(tableStartX, headerY, tableWidth, headerHeight, "FD");

      // Header text
      doc.text("Opponent", tableStartX + col1Width / 2, headerY + headerHeight / 2 + 2, { align: "center" });
      doc.text("Win/Loss", tableStartX + col1Width + col2Width / 2, headerY + headerHeight / 2 + 2, {
        align: "center",
      });
      doc.text("Score", tableStartX + col1Width + col2Width + col3Width / 2, headerY + headerHeight / 2 + 2, {
        align: "center",
      });

      // Draw header borders
      doc.line(tableStartX, headerY, tableStartX + tableWidth, headerY); // Top
      doc.line(tableStartX, headerY + headerHeight, tableStartX + tableWidth, headerY + headerHeight); // Bottom
      doc.line(tableStartX, headerY, tableStartX, headerY + headerHeight); // Left border
      doc.line(tableStartX + tableWidth, headerY, tableStartX + tableWidth, headerY + headerHeight); // Right border
      doc.line(tableStartX + col1Width, headerY, tableStartX + col1Width, headerY + headerHeight); // Col 1-2 divider
      doc.line(
        tableStartX + col1Width + col2Width,
        headerY,
        tableStartX + col1Width + col2Width,
        headerY + headerHeight,
      ); // Col 2-3 divider

      // Draw table rows for each opposing entry, plus a blank row at the bottom
      doc.setFont("helvetica", "normal");
      let currentY = headerY + headerHeight;

      const drawOpponentRow = (opponentName: string) => {
        // Check if we need a new page
        if (currentY + rowHeight > pageHeight - margin) {
          doc.addPage();
          currentY = margin + 20; // Reset Y position for new page

          // Redraw header on new page
          doc.setFontSize(14); // Increased font size for headers
          doc.setFont("helvetica", "bold");
          doc.setFillColor(240, 240, 240);
          doc.rect(tableStartX, currentY, tableWidth, headerHeight, "FD");
          doc.text("Opponent", tableStartX + col1Width / 2, currentY + headerHeight / 2 + 2, {
            align: "center",
          });
          doc.text("Win/Loss", tableStartX + col1Width + col2Width / 2, currentY + headerHeight / 2 + 2, {
            align: "center",
          });
          doc.text(
            "Score",
            tableStartX + col1Width + col2Width + col3Width / 2,
            currentY + headerHeight / 2 + 2,
            {
              align: "center",
            },
          );
          doc.line(tableStartX, currentY, tableStartX + tableWidth, currentY);
          doc.line(tableStartX, currentY + headerHeight, tableStartX + tableWidth, currentY + headerHeight);
          doc.line(tableStartX, currentY, tableStartX, currentY + headerHeight); // Left border
          doc.line(tableStartX + tableWidth, currentY, tableStartX + tableWidth, currentY + headerHeight); // Right border
          doc.line(tableStartX + col1Width, currentY, tableStartX + col1Width, currentY + headerHeight);
          doc.line(
            tableStartX + col1Width + col2Width,
            currentY,
            tableStartX + col1Width + col2Width,
            currentY + headerHeight,
          );
          doc.setFont("helvetica", "normal");
          doc.setFontSize(10); // Reset to default
          currentY += headerHeight;
        }

        // Draw row borders
        doc.line(tableStartX, currentY, tableStartX + tableWidth, currentY); // Top border
        doc.line(tableStartX, currentY + rowHeight, tableStartX + tableWidth, currentY + rowHeight); // Bottom border
        doc.line(tableStartX, currentY, tableStartX, currentY + rowHeight); // Left border
        doc.line(tableStartX + tableWidth, currentY, tableStartX + tableWidth, currentY + rowHeight); // Right border
        doc.line(tableStartX + col1Width, currentY, tableStartX + col1Width, currentY + rowHeight); // Col 1-2 divider
        doc.line(
          tableStartX + col1Width + col2Width,
          currentY,
          tableStartX + col1Width + col2Width,
          currentY + rowHeight,
        ); // Col 2-3 divider

        if (opponentName) {
          doc.setFontSize(12); // Increased font size for opponent names
          doc.text(opponentName, tableStartX + 5, currentY + rowHeight / 2 + 2);
          doc.setFontSize(10); // Reset to default
        }
        // Columns 2 and 3 are left blank

        currentY += rowHeight;
      };

      for (const match of entryMatches) {
        const opposingEntryId = match.entries?.current.find((id) => id !== entryId);
        const opposingEntry = opposingEntryId ? this.state.entries.findById(opposingEntryId) : null;
        const opponentName = opposingEntry?.names.join(" & ") || "";
        drawOpponentRow(opponentName);
      }

      // Always leave a blank opponent line for handwriting extras
      drawOpponentRow("");

      // Draw final bottom border
      doc.line(tableStartX, currentY, tableStartX + tableWidth, currentY);

      doc.addPage();
    }
    doc.deletePage(doc.getCurrentPageInfo().pageNumber);
    doc.save("a4.pdf");

    if (
      this.state.lockGroupsAfterPrint &&
      this.state.competition.format === "RoundRobin" &&
      roundRobinHasGroups(this.state.competition) &&
      !roundRobinGroupsLocked(this.state.competition)
    ) {
      try {
        await setRoundRobinGroupsLocked(this.state.competition, true);
        messagesService.addMessage(
          "Groups are locked. They will stay as they are unless you move someone by hand.",
          "success",
        );
      } catch (error) {
        messagesService.addMessage(
          `Printed, but groups could not be locked: ${error instanceof Error ? error.message : "Unknown error"}`,
          "error",
        );
      }
    }

    this.actionCloseModal();
  }
}

const CompetitionPrintModal = () => {
  const controller = CompetitionPrintController.use();
  const { loading, orientation, paperSize, competition, lockGroupsAfterPrint } = controller.state;
  const groupsLocked = roundRobinGroupsLocked(competition);
  const canLockGroups = competition.format === "RoundRobin" && roundRobinHasGroups(competition);

  if (loading) {
    return <LoadingModal title="Print" />;
  }

  const printableCount = controller.getPrintableEntryCount();

  return (
    <AppModal title={`Print ${printableCount} groups`}>
      <div className="mb-4 space-y-4">
        <div>
          <div className="block mb-2 text-sm font-medium">Page Orientation</div>
          <Select
            name="orientation"
            value={orientation}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
              controller.state.orientation = e.target.value as "landscape" | "portrait";
            }}
          >
            <option value="landscape">Landscape</option>
            <option value="portrait">Portrait</option>
          </Select>
        </div>
        <div>
          <div className="block mb-2 text-sm font-medium">Paper Size</div>
          <Select
            name="paperSize"
            value={paperSize}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
              controller.state.paperSize = e.target.value as "a4" | "letter" | "a5";
            }}
          >
            <option value="a4">A4</option>
            <option value="a5">A5</option>
            <option value="letter">Letter (US)</option>
          </Select>
        </div>
        {canLockGroups && !groupsLocked && (
          <div className="flex items-start gap-2 text-sm">
            <Checkbox
              id="lock-groups-after-print"
              className="mt-0.5"
              checked={lockGroupsAfterPrint}
              onChange={(e) => {
                controller.state.lockGroupsAfterPrint = e.target.checked;
              }}
            />
            <label htmlFor="lock-groups-after-print">
              Lock groups after download so they stay as printed. You can still move people by hand.
            </label>
          </div>
        )}
        {groupsLocked && (
          <p className="text-sm text-gray-600">Groups are already locked and will not be rearranged.</p>
        )}
      </div>
      <ModalFooter>
        <div className="flex justify-end w-full">
          <Button kind="primary" disabled={printableCount === 0} onClick={() => controller.actionPrint()}>
            Download PDF
          </Button>
        </div>
      </ModalFooter>
    </AppModal>
  );
};

export default injectModal(CompetitionPrintController.scope(ApplicationView(CompetitionPrintModal)));
